// Escolha/envio de imagem para o anúncio (jpg, png ou webp até 15 MB).
import { useRef } from 'react'
import { Check, ImagePlus, RefreshCw, Trash2 } from 'lucide-react'
import { ASSET_ACCEPT, ASSET_MAX_BYTES } from '@impulso/shared/api/builder'
import { errorMessage } from '../../lib/api'
import { useConfirm } from '../../ui/confirm'
import { useToast } from '../../ui/toast'
import { useAssets, useDeleteAsset, useUploadAsset } from './api'

export interface ImagePickerProps {
  companyId: string
  value: string | null | undefined
  onChange: (assetId: string | null) => void
  canEdit: boolean
}

export default function ImagePicker({ companyId, value, onChange, canEdit }: ImagePickerProps) {
  const toast = useToast()
  const confirm = useConfirm()
  const input = useRef<HTMLInputElement>(null)
  const q = useAssets(companyId)
  const upload = useUploadAsset(companyId)
  const remove = useDeleteAsset(companyId)

  const onFile = (file: File | undefined) => {
    if (!file) return
    if (file.size > ASSET_MAX_BYTES) return toast('A imagem passa de 15 MB. Envie uma imagem menor.')
    upload.mutate(file, {
      onSuccess: (a) => { onChange(a.id); toast('Imagem enviada.') },
      onError: (err) => toast(errorMessage(err)),
    })
  }

  const items = q.data?.items ?? []
  return (
    <div className="stack" style={{ gap: 8 }}>
      {q.isLoading && <div className="row" style={{ gap: 10 }}>{[0, 1, 2].map((i) => <div key={i} className="skeleton thumb" style={{ width: 76, height: 76 }} />)}</div>}
      {q.isError && <div className="form-error">Não conseguimos carregar suas imagens. <button className="btn ghost sm" onClick={() => q.refetch()}>Tentar de novo</button></div>}
      <div className="row wrap" style={{ gap: 10 }}>
        {items.map((a) => (
          <div key={a.id} style={{ position: 'relative' }}>
            <button type="button" onClick={() => onChange(value === a.id ? null : a.id)} className="thumb" title={a.originalName ?? 'Imagem'} aria-pressed={value === a.id}
              style={{ width: 76, height: 76, padding: 0, overflow: 'hidden', border: value === a.id ? '3px solid var(--primary)' : '3px solid transparent', background: '#eceef2' }}>
              <img src={a.url} alt={a.originalName ?? 'Imagem do anúncio'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              {value === a.id && <span style={{ position: 'absolute', top: 4, left: 4, background: 'var(--primary)', borderRadius: 999, display: 'grid', placeItems: 'center', width: 20, height: 20 }}><Check size={13} color="white" /></span>}
            </button>
            {canEdit && value !== a.id && (
              <button type="button" className="icon-btn" aria-label="Apagar imagem" title="Apagar imagem" disabled={remove.isPending}
                onClick={async () => { if (await confirm({ title: 'Apagar esta imagem?', confirmLabel: 'Apagar', danger: true })) remove.mutate(a.id, { onError: (err) => toast(errorMessage(err)) }) }}
                style={{ position: 'absolute', right: -6, top: -6, width: 24, height: 24, borderRadius: 999, background: 'white', border: '1px solid var(--border)', display: 'grid', placeItems: 'center' }}>
                <Trash2 size={12} />
              </button>
            )}
          </div>
        ))}
        {canEdit && (
          <button type="button" className="thumb" onClick={() => input.current?.click()} disabled={upload.isPending} aria-label="Enviar imagem"
            style={{ width: 76, height: 76, border: '2px dashed var(--border-strong)', background: 'white', color: 'var(--muted)' }}>
            {upload.isPending ? <RefreshCw size={20} className="spin" /> : <ImagePlus size={22} />}
          </button>
        )}
      </div>
      {!q.isLoading && items.length === 0 && <p className="small muted">Você ainda não enviou imagens. Use uma foto bonita do produto, de preferência quadrada (1080 × 1080).</p>}
      <input ref={input} type="file" accept={ASSET_ACCEPT} hidden onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = '' }} />
      <p className="small muted">JPG, PNG ou WebP, até 15 MB.</p>
    </div>
  )
}
